import React, { FC, useMemo } from 'react';

import ComponentTypesIconWrapper from '../../../../components/componentTypesIcon/ComponentTypesIconWrapper';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';

import { createShouldForwardProp } from '../../../../helpers/shouldForwardProp';

import useComponentTypesQuery from '../../../../hooks/component-types/useComponentTypesQuery';
import useDatasetByIdQuery from '../../../../hooks/datasets/useDatasetByIdQuery';
import useDatasetColumnComponentTypesQuery from '../../../../hooks/datasets/useDatasetColumnComponentTypesQuery';
import useDatasetLabelColumnsQuery from '../../../../hooks/datasets/useDatasetLabelColumnsQuery';

import { ComponentTypeMapped } from '../../../../models/component-types-model';

import DatasetAlternateLabelView from './DatasetAlternateLabelView';
import DatasetComponentNote from './DatasetComponentNote';

const shouldForwardProps = createShouldForwardProp([
    'bgColor',
    'isBorderRight',
    'hoverBgColor',
    'isTopHeader',
]);

interface StyledProps {
    bgColor?: string;
    isBorderRight?: boolean;
    hoverBgColor?: string;
    isTopHeader?: boolean;
    index?: number;
}

interface DatasetComponentViewProps {
    datasetId: number;
    datasetField: number;
    columnNameMapping: Record<number, string>;
    wrapText?: boolean;
}

const DatasetComponentView: FC<DatasetComponentViewProps> = ({
    datasetId,
    datasetField,
    columnNameMapping,
    wrapText,
}) => {
    const { data: dataset } = useDatasetByIdQuery(datasetId);
    const { data: componentTypes } = useComponentTypesQuery();
    const { data: columnComponentTypes } =
        useDatasetColumnComponentTypesQuery(datasetId);

    const { data: labelColumns } = useDatasetLabelColumnsQuery(datasetId);

    const componentType = useMemo<ComponentTypeMapped | undefined>(() => {
        const componentId = columnComponentTypes?.[datasetField];
        return componentId ? componentTypes?.mapById[componentId] : undefined;
    }, [columnComponentTypes, componentTypes, datasetField]);

    const Icon = useMemo(
        () => <ComponentTypesIconWrapper dataKey={componentType?.data_key} />,
        [componentType],
    );

    const labelColumnId = useMemo<number | undefined>(
        () => labelColumns?.[datasetField],
        [labelColumns, datasetField],
    );

    return (
        <Container>
            <HeaderTextContainer
                bgColor={componentType?.secondary_color}
                hoverBgColor={componentType?.medium_color}
            >
                <HeaderText
                    bgColor={componentType?.secondary_color}
                    hoverBgColor={componentType?.medium_color}
                    isTopHeader
                >
                    <HeaderBody>
                        {Icon}
                        {componentType?.name} {'(' + dataset?.count_rows + ')'}
                    </HeaderBody>
                    <StyledDatasetName>
                        <ICONS.datasets size={16} />
                        {dataset?.name}
                    </StyledDatasetName>
                </HeaderText>
            </HeaderTextContainer>
            <HeaderContainer>
                <HeaderText isBorderRight>
                    {columnNameMapping[datasetField]}{' '}
                    <AlternateLabelTag>Alternate</AlternateLabelTag>
                </HeaderText>
                <HeaderText>
                    {labelColumnId ? (
                        <>
                            {columnNameMapping[labelColumnId]}
                            <AlternateLabelTag>Label</AlternateLabelTag>
                        </>
                    ) : (
                        'Label'
                    )}
                </HeaderText>
            </HeaderContainer>
            <DatasetAlternateLabelView
                datasetId={datasetId}
                datasetField={datasetField}
                wrapText={wrapText}
            />
            <DatasetComponentNote
                datasetId={datasetId}
                note={'Dataset Components cannot be edited here.'}
            />
        </Container>
    );
};

const Container = styled.div({
    width: '500px',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
});

const HeaderTextContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledProps>(({ bgColor, hoverBgColor }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    backgroundColor: bgColor ?? aampeTheme.colors.backgroundWG02,
    '&:hover': {
        backgroundColor: hoverBgColor ?? aampeTheme.colors.backgroundWG02,
    },
}));

const HeaderText = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledProps>(({ bgColor, isBorderRight, isTopHeader }) => ({
    display: 'flex',
    flex: isBorderRight ? 1 : 'none',
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
    padding: aampeTheme.spacing4(2, 4),
    fontSize: aampeTheme.size.font.textMedium,
    lineHeight: '24px',
    fontWeight: 500,
    color: aampeTheme.colors.textAccessibleWG01,
    width: isTopHeader ? '100%' : isBorderRight ? '' : '250px',
    overflow: 'hidden',
    height: '43px',
    backgroundColor: 'inherit',
    borderRight: isBorderRight
        ? `1px solid ${bgColor ?? aampeTheme.colors.interactiveWG01}`
        : '0px',
    borderBottom: '0px',
    borderLeft: '0px',
}));

const HeaderBody = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(2),
});

const StyledDatasetName = styled.span({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
    padding: aampeTheme.spacing4(1, 2),
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid ${aampeTheme.colors.solidA02}`,
    background: aampeTheme.colors.interactiveWG01,
});

const HeaderContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    borderBottom: `0px`,
    cursor: 'pointer',
});

const AlternateLabelTag = styled.span({
    borderRadius: aampeTheme.spacing4(1),
    padding: aampeTheme.spacing4(0.5, 1),
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    color: aampeTheme.colors.textAccessibleWG01,
});

export default DatasetComponentView;
